import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import Card from '../common/Card';
import Table from '../common/Table';
import Button from '../common/Button';
import Modal from '../common/Modal';
import Input from '../common/Input';
import api from '../../services/api';
import { formatDate, formatCurrency } from '../../utils/formatters';
import '../../styles/admin.css';

const BusinessList = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showModal, setShowModal]   = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [plans, setPlans]           = useState([]);
  const [error, setError]           = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') || 'all';
  const [notifying, setNotifying]   = useState(false);

  useEffect(() => {
    loadPlans();
  }, []);

  useEffect(() => {
    loadBusinesses();
  }, [statusFilter]);

  const handleNotifyExpired = async () => {
    setNotifying(true);
    try {
      const res = await api.post('/admin/trials/notify-expired');
      if ((res.data.failed || 0) > 0) {
        toast.error(res.data.message + ' — check SMTP settings (MAIL_* in .env)');
      } else {
        toast.success(res.data.message);
      }
      loadBusinesses();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send trial-ended emails');
    } finally {
      setNotifying(false);
    }
  };

  const loadBusinesses = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/businesses', {
        params: { status: statusFilter, limit: 100 }
      });
      setBusinesses(response.data.businesses || []);
    } catch (error) {
      console.error('Failed to load businesses:', error);
      setError('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  const loadPlans = async () => {
    try {
      const response = await api.get('/admin/plans'); // FIX: .php
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Failed to load plans:', error);
    }
  };

  const handleStatusChange = async (businessId, newStatus) => {
    try {
      await api.put(`/admin/subscription/${businessId}`, { // FIX: .php + correct endpoint
        status: newStatus
      });
      loadBusinesses();
    } catch (error) {
      console.error('Failed to update status:', error);
      alert(error.response?.data?.error || 'Failed to update status');
    }
  };

  const handleEdit = (business) => {
    setSelectedBusiness(business);
    setShowModal(true);
  };

  const handleCreate = () => {
    setSelectedBusiness({ isNew: true });
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    try {
      if (selectedBusiness.isNew) {
        await api.post(`/admin/businesses`, formData);
      } else {
        await api.put(`/admin/businesses/${selectedBusiness.id}`, formData);
      }
      setShowModal(false);
      setSelectedBusiness(null);
      loadBusinesses();
    } catch (error) {
      console.error('Failed to update business:', error);
      alert(error.response?.data?.error || 'Failed to update business');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure? This will suspend the business if it has data.')) return;
    try {
      await api.delete(`/admin/businesses/${id}`); // FIX: .php
      loadBusinesses();
    } catch (error) {
      console.error('Failed to delete business:', error);
      alert(error.response?.data?.error || 'Failed to delete business');
    }
  };

  const columns = [
    {
      key: 'business_name',
      label: 'Business',
      render: (value, row) => (
        <div className="business-cell">
          <div className="business-name">{value}</div>
          <div className="business-email">{row.business_email}</div>
        </div>
      )
    },
    {
      key: 'plan_name',
      label: 'Plan',
      render: (value) => value || 'No Plan'
    },
    {
      key: 'subscription_status',
      label: 'Status',
      render: (value, row) => (
        row.trial_expired ? (
          <span className="status-badge trial-ended" title={`Trial ended ${formatDate(row.trial_ends_at)}`}>
            Trial ended
          </span>
        ) : (
          <span className={`status-badge ${value}`}>{value}</span>
        )
      )
    },
    {
      key: 'trial_ends_at',
      label: 'Trial Ends',
      render: (value, row) => (
        row.subscription_status === 'trial' && value ? formatDate(value) : '—'
      )
    },
    {
      key: 'user_count',
      label: 'Users',
      render: (value) => value || 0
    },
    {
      key: 'product_count',
      label: 'Products',
      render: (value) => value || 0
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value) => formatDate(value)
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, row) => (
        <div className="action-buttons">
          <select
            className="status-select"
            value={row.subscription_status}
            onChange={(e) => handleStatusChange(row.id, e.target.value)}
          >
            <option value="active">Active</option>
            <option value="trial">Trial</option>
            <option value="suspended">Suspended</option>
            <option value="inactive">Inactive</option>
          </select>
          <Button size="sm" variant="secondary" onClick={() => handleEdit(row)}>
            Edit
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={() => navigate(`/admin/businesses/${row.id}`)}
          >
            View
          </Button>
          <Button size="sm" variant="danger" onClick={() => handleDelete(row.id)}>
            Delete
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="business-list">
      <div className="page-header">
        <h1>Business Management</h1>
        <div className="header-actions" style={{display: 'flex', gap: '1rem', flexWrap: 'wrap'}}>
          <select
            className="status-select"
            value={statusFilter}
            onChange={(e) => setSearchParams(e.target.value === 'all' ? {} : { status: e.target.value })}
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="trial">Trial (running)</option>
            <option value="trial_ended">Trial ended</option>
            <option value="suspended">Suspended</option>
            <option value="inactive">Inactive</option>
          </select>
          <Button variant="secondary" onClick={handleNotifyExpired} loading={notifying}>
            Email trial-ended businesses
          </Button>
          <Button variant="primary" onClick={handleCreate}>
            + Create Business
          </Button>
          <Button variant="secondary" onClick={loadBusinesses}>
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <Button size="sm" variant="secondary" onClick={loadBusinesses}>Retry</Button>
        </div>
      )}

      <Card>
        <Table
          columns={columns}
          data={businesses}
          loading={loading}
          emptyMessage="No businesses found."
        />
      </Card>

      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setSelectedBusiness(null); }}
        title={selectedBusiness?.isNew ? "Create Business" : "Edit Business"}
      >
        {selectedBusiness && (
          <BusinessForm
            business={selectedBusiness}
            plans={plans}
            onSave={handleSave}
            onCancel={() => { setShowModal(false); setSelectedBusiness(null); }}
          />
        )}
      </Modal>
    </div>
  );
};

const BusinessForm = ({ business, plans, onSave, onCancel }) => {
  const isNew = business?.isNew || false;
  const [formData, setFormData] = useState({
    business_name:        business?.business_name    || '',
    business_email:       business?.business_email   || '',
    business_phone:       business?.business_phone   || '',
    business_address:     business?.business_address || '',
    subscription_plan_id: business?.subscription_plan_id || '',
    tax_number:           business?.tax_number       || '',
    // Admin fields (only for creation)
    admin_first_name:     '',
    admin_last_name:      '',
    admin_email:          '',
    admin_password:       '',
    admin_phone:          ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="business-form">
      <h4>Business Details</h4>
      <Input label="Business Name"  name="business_name"  value={formData.business_name}  onChange={handleChange} required />
      <Input label="Business Email" name="business_email" value={formData.business_email} onChange={handleChange} required type="email" />
      <Input label="Phone"          name="business_phone" value={formData.business_phone} onChange={handleChange} />
      <Input label="Address"        name="business_address" value={formData.business_address} onChange={handleChange} />
      <Input label="Tax Number"     name="tax_number"     value={formData.tax_number}     onChange={handleChange} />
      <div className="form-group">
        <label>Subscription Plan</label>
        <select name="subscription_plan_id" value={formData.subscription_plan_id} onChange={handleChange}>
          <option value="">Select Plan</option>
          {plans.map(plan => (
            <option key={plan.id} value={plan.id}>
              {plan.plan_name} — {formatCurrency(plan.price_monthly)}/month
            </option>
          ))}
        </select>
      </div>

      {isNew && (
        <>
          <hr style={{margin: '20px 0', border: '1px solid #eee'}} />
          <h4>Initial Admin Details</h4>
          <Input label="Admin First Name" name="admin_first_name" value={formData.admin_first_name} onChange={handleChange} required />
          <Input label="Admin Last Name"  name="admin_last_name"  value={formData.admin_last_name}  onChange={handleChange} required />
          <Input label="Admin Email (Login)" name="admin_email"   value={formData.admin_email}      onChange={handleChange} required type="email" />
          <Input label="Admin Phone"      name="admin_phone"      value={formData.admin_phone}      onChange={handleChange} />
          <Input label="Admin Password"   name="admin_password"   value={formData.admin_password}   onChange={handleChange} required type="password" />
        </>
      )}

      <div className="form-actions" style={{marginTop: '20px'}}>
        <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="primary" loading={saving}>Save Changes</Button>
      </div>
    </form>
  );
};

export default BusinessList;